'use client';

import { Button } from '@common/components';
import { useToast } from '@common/hooks';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { CUSTOM_SCHEME } from '@/modules/wello-app';

import { useBenefitGate } from '../../_hooks/useBenefitGate';
import { useCountdown } from '../../_hooks/useCountdown';
import { formatCountdown } from '../../_utils/countdown';
import { useClaimCoupang, useCoupangEarn } from '../_hooks/useCoupangEarn';

const COUPANG_PARTNERS_LINK = 'https://link.coupang.com/a/fnpfimVDsO';

interface CoupangEarnCountdownButtonProps {
  onClickEarn: () => void;
  isPending: boolean;
}

//* rewardPoint를 아직 모르면(서버 미제공) 숫자 없이 중립 라벨로 노출한다.
const resolveEarnLabel = (rewardPoint: number | null) =>
  rewardPoint !== null
    ? `쿠팡에서 ${rewardPoint}P 받기`
    : '쿠팡에서 포인트 받기';

const CoupangEarnCountdownButton = ({
  onClickEarn,
  isPending,
}: CoupangEarnCountdownButtonProps) => {
  const { data } = useCoupangEarn();
  const { isAvailable, secondsUntilAvailable } = useCountdown(
    data.nextAvailableAtMs,
  );
  const earnLabel = resolveEarnLabel(data.rewardPoint);

  return (
    <Button
      className="flex-1"
      disabled={!isAvailable || isPending}
      onClick={onClickEarn}
    >
      {isAvailable ? (
        earnLabel
      ) : (
        //* 클라 시계 기반 초 표시 — SSR/클라 시각 차 하이드레이션 경고를 억제.
        <span suppressHydrationWarning className="tabular-nums">
          {formatCountdown(secondsUntilAvailable)} 뒤에 참여 가능해요
        </span>
      )}
    </Button>
  );
};

interface CoupangEarnButtonProps {
  isGuest: boolean;
}

export const CoupangEarnButton = ({ isGuest }: CoupangEarnButtonProps) => {
  const { mutate: claim, isPending } = useClaimCoupang();
  const { triggerGate, gateModals } = useBenefitGate();
  const { isWelloApp } = useCheckWelloApp();
  const { toast } = useToast();

  const handleClickEarn = () => {
    triggerGate(['app', 'login', 'consent'], () => {
      //* 앱은 네이티브가 external-app:: 스킴을 외부 브라우저로 가로채고, 웹은 새 탭으로 연다.
      if (isWelloApp) {
        window.location.href = `${CUSTOM_SCHEME.EXTERNAL_PREFIX}${COUPANG_PARTNERS_LINK}`;
      } else {
        window.open(COUPANG_PARTNERS_LINK, '_blank', 'noopener,noreferrer');
      }

      claim(
        {},
        {
          onSuccess: (response) => {
            const addedPoints = response.context?.addPoints;
            toast({
              message: addedPoints
                ? `${addedPoints}P가 적립됐어요`
                : '적립됐어요',
            });
          },
        },
      );
    });
  };

  return (
    <>
      {isGuest ? (
        <Button className="flex-1" onClick={handleClickEarn}>
          쿠팡에서 포인트 받기
        </Button>
      ) : (
        <ErrorBoundary
          fallback={
            <Button className="flex-1" disabled>
              쿠팡에서 포인트 받기
            </Button>
          }
        >
          <Suspense
            fallback={
              <Button className="flex-1" disabled>
                쿠팡에서 포인트 받기
              </Button>
            }
          >
            <CoupangEarnCountdownButton
              onClickEarn={handleClickEarn}
              isPending={isPending}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {gateModals}
    </>
  );
};
